import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { LiveActivityAction, LiveActivitySummary } from '../../_models/admin/live-activity.models';

/**
 * Typed calls to api/LiveActivity: the activity summary for a window and one user's recent actions. The API
 * is admin-only, read-only, and returns 404 while the admin.live-activity switch is off.
 *
 * Follows the shared BaseService pattern. prepareOptions() hashes the request body into the RequestMatch
 * header; both calls are GETs with no body, so they use prepareOptions(null). The query string is not part
 * of the hash. prepareOptions() also sets observe:'response', so responses are unwrapped.
 */
@Injectable({ providedIn: 'root' })
export class LiveActivityService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  public getSummary(minutes: number): Observable<LiveActivitySummary> {
    return this.http.get(`${this.apiUrl}api/LiveActivity/Summary?minutes=${encodeURIComponent(minutes)}`, this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as LiveActivitySummary),
      catchError(this.handleError('getSummary'))
    );
  }

  public getUserRecent(userId: number, take: number = 50): Observable<LiveActivityAction[]> {
    const url = `${this.apiUrl}api/LiveActivity/Users/${encodeURIComponent(userId)}/Recent?take=${encodeURIComponent(take)}`;
    return this.http.get(url, this.prepareOptions(null)).pipe(
      map((data: any) => (this.unwrap(data) || []) as LiveActivityAction[]),
      catchError(this.handleError('getUserRecent'))
    );
  }

  /** Shared body unwrap — prepareOptions() observes the full response, not just the body. */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
